import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Doughnut } from 'react-chartjs-2';

import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import WcRoundedIcon from '@mui/icons-material/WcRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import CakeRoundedIcon from '@mui/icons-material/CakeRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import InterestsRoundedIcon from '@mui/icons-material/InterestsRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import LoyaltyRoundedIcon from '@mui/icons-material/LoyaltyRounded';

import ActivewearIcon from 'assets/planner/Activewear.png';
import ArtDesignIcon from 'assets/planner/Art-and-Design.png';
import BeautyCosmeticIcon from 'assets/planner/Beauty-and-Cosmetics.png';
import BeerWineIcon from 'assets/planner/Beer-Wine-&-Spirits.png';
import BusinessCareerIcon from 'assets/planner/Business-and-Careers.png';
import CameraPhotographyIcon from 'assets/planner/Camera-&-Photography.png';
import CarMotorbikeIcon from 'assets/planner/Cars-&-Motorbikes.png';
import ClothesShoesIcon from 'assets/planner/Clothes-Shoes.png';
import CoffeeTeaIcon from 'assets/planner/Coffee-Tea-&-Beverages.png';
import ElectronicsComputerIcon from 'assets/planner/Electronics-and-Computers.png';
import FitnessYogaIcon from 'assets/planner/Yoga.png';
import FriendsFamilyIcon from 'assets/planner/Friends-Family-&-Relationships.png';
import GamingIcon from 'assets/planner/Gaming.png';
import HealthcareMedicineIcon from 'assets/planner/Healthcare-and-Medicine.png';
import HealthyLifestyleIcon from 'assets/planner/Healthy-Lifestyle.png';
import HomeDecorIcon from 'assets/planner/Home-Decor-Furniture-&-Garden.png';
import JewlleryWatchesIcon from 'assets/planner/Jewellery-&-Watches.png';
import LuxaryGoodsIcon from 'assets/planner/Luxury-Goods.png';
import MusicIcon from 'assets/planner/Music.png';
import PetsIcon from 'assets/planner/Pets.png';
import RestaurantFoodIcon from 'assets/planner/Restaurants-Food-&-Grocery.png';
import ShoppingRetail from 'assets/planner/Shopping-&-Retail.png';
import SportsIcon from 'assets/planner/Sports.png';
import TelevisionFilmsIcon from 'assets/planner/Television-and-Film.png';
import TobaccoSmokingIcon from 'assets/planner/Tobacco-&-Smoking.png';
import ToysIcon from 'assets/planner/Toys-Children-&-Baby.png';
import TravelTourismIcon from 'assets/planner/Travel-Tourism-and-Aviation.png';
import WeddingIcon from 'assets/planner/Wedding.png';

import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useEffect, useState } from 'react';
import { Chip } from '@mui/material';

import { SectionCard, ChartTile, EmptyState, ui } from '../shared/insightUi';

const audienceIcons = {
  'Television & Film': TelevisionFilmsIcon,
  Music: MusicIcon,
  'Shopping & Retail': ShoppingRetail,
  'Coffee, Tea & Beverages': CoffeeTeaIcon,
  'Camera & Photography': CameraPhotographyIcon,
  'Clothes, Shoes, Handbags & Accessories': ClothesShoesIcon,
  'Beer, Wine & Spirits': BeerWineIcon,
  Sports: SportsIcon,
  'Electronics & Computers': ElectronicsComputerIcon,
  Gaming: GamingIcon,
  Activewear: ActivewearIcon,
  'Art & Design': ArtDesignIcon,
  'Travel, Tourism & Aviation': TravelTourismIcon,
  'Business & Careers': BusinessCareerIcon,
  'Beauty & Cosmetics': BeautyCosmeticIcon,
  'Healthcare & Medicine': HealthcareMedicineIcon,
  'Jewellery & Watches': JewlleryWatchesIcon,
  'Restaurants, Food & Grocery': RestaurantFoodIcon,
  'Toys, Children & Baby': ToysIcon,
  'Fitness & Yoga': FitnessYogaIcon,
  Wedding: WeddingIcon,
  'Tobacco & Smoking': TobaccoSmokingIcon,
  Pets: PetsIcon,
  'Healthy Lifestyle': HealthyLifestyleIcon,
  'Luxury Goods': LuxaryGoodsIcon,
  'Home Decor, Furniture & Garden': HomeDecorIcon,
  'Friends, Family & Relationships': FriendsFamilyIcon,
  'Cars & Motorbikes': CarMotorbikeIcon,
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const AGE_BUCKET_TO_LABEL = {
  age18To24: '18-24',
  age25To34: '25-34',
  age35To44: '35-44',
  age45To54: '45-54',
  age55To64: '55-64',
  age65ToInf: '65+',
};

function normalizeAgeSplit(raw) {
  if (!raw) return [];
  if (Array.isArray(raw) && raw.every((x) => x?.code != null && x?.weight != null)) return raw;
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return Object.entries(raw)
      .filter(([, w]) => w > 0)
      .map(([bucket, weight]) => ({
        code: AGE_BUCKET_TO_LABEL[bucket] || bucket.replace(/^age/, '').replace(/To/g, '-'),
        weight,
      }));
  }
  return [];
}

function normalizeLocationSplit(raw) {
  if (!raw) return { labels: [], data: [] };

  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0];
    if (first?.regionCode != null && first?.percentage != null) {
      return {
        labels: raw.map((x) => x.regionCode),
        data: raw.map((x) => Math.round((x.percentage ?? 0) * 100)),
      };
    }
    if (first?.region_code != null && (first?.percentage != null || first?.weight != null)) {
      return {
        labels: raw.map((x) => x.region_code || x.regionCode),
        data: raw.map((x) => Math.round((x.percentage ?? x.weight ?? 0) * 100)),
      };
    }
  }

  if (raw?.cities && Array.isArray(raw.cities)) {
    return {
      labels: raw.cities.map((x) => x.name || x.region_code),
      data: raw.cities.map((x) => Math.round((x.weight ?? x.percentage ?? 0) * 100)),
    };
  }
  if (raw?.countries && Array.isArray(raw.countries)) {
    return {
      labels: raw.countries.map((x) => x.name || x.region_code),
      data: raw.countries.map((x) => Math.round((x.weight ?? x.percentage ?? 0) * 100)),
    };
  }

  return { labels: [], data: [] };
}

function normalizeStateSplit(audienceCountryDistributions) {
  if (!Array.isArray(audienceCountryDistributions)) return { labels: [], data: [] };

  const indiaEntry = audienceCountryDistributions.find((x) => x.regionCode === 'IN');
  if (!indiaEntry || !Array.isArray(indiaEntry.regions) || indiaEntry.regions.length === 0) {
    return { labels: [], data: [] };
  }

  const sorted = [...indiaEntry.regions].sort((a, b) => b.percentage - a.percentage);
  return {
    labels: sorted.map((x) => x.regionCode.replace(/^IN-/, '')),
    data: sorted.map((x) => Math.round(x.percentage * 100)),
  };
}

function normalizeGenderSplit(raw, bcInsight, channelStatsKey) {
  const toWeight = (v) => {
    if (v == null) return null;
    if (typeof v === 'string') {
      const num = parseFloat(v.replace('%', ''));
      if (isNaN(num)) return null;
      return v.includes('%') ? num / 100 : num > 1 ? num / 100 : num;
    }
    if (typeof v === 'number') return v > 1 ? v / 100 : v;
    return null;
  };

  if (Array.isArray(raw) && raw.length > 0) {
    const normalized = raw
      .map((x) => {
        const code = x?.code ?? x?.gender ?? x?.name;
        const weight = toWeight(x?.weight ?? x?.percentage);
        return code && weight != null ? { code, weight } : null;
      })
      .filter(Boolean);
    if (normalized.length > 0) return normalized;
  }

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const male = toWeight(raw.male ?? raw.MALE ?? raw.M);
    const female = toWeight(raw.female ?? raw.FEMALE ?? raw.F);
    const userSpecified = toWeight(raw.userSpecified ?? raw.USER_SPECIFIED ?? raw.user_specified);
    if (male != null || female != null || userSpecified != null) {
      return [
        ...(male > 0 ? [{ code: 'MALE', weight: male }] : []),
        ...(female > 0 ? [{ code: 'FEMALE', weight: female }] : []),
        ...(userSpecified > 0 ? [{ code: 'USER_SPECIFIED', weight: userSpecified }] : []),
      ];
    }

    const values = Object.values(raw);
    const normalized = values
      .map((x) => {
        const code = x?.code ?? x?.gender ?? x?.name;
        const weight = toWeight(x?.weight ?? x?.percentage);
        return code && weight != null ? { code, weight } : null;
      })
      .filter(Boolean);
    if (normalized.length > 0) return normalized;
  }

  const dist = bcInsight?.channelStats?.[channelStatsKey]?.audienceGenderDistribution;
  const male = toWeight(dist?.male ?? dist?.MALE);
  const female = toWeight(dist?.female ?? dist?.FEMALE);
  const userSpecified = toWeight(dist?.userSpecified ?? dist?.USER_SPECIFIED ?? dist?.user_specified);
  if (male != null || female != null || userSpecified != null) {
    return [
      ...(male > 0 ? [{ code: 'MALE', weight: male }] : []),
      ...(female > 0 ? [{ code: 'FEMALE', weight: female }] : []),
      ...(userSpecified > 0 ? [{ code: 'USER_SPECIFIED', weight: userSpecified }] : []),
    ];
  }

  return [];
}

function normalizeInterestForBar(source) {
  if (!source) return { labels: [], data: [] };
  if (Array.isArray(source) && source.length > 0) {
    const first = source[0];
    if (first?.label != null || first?.name != null) {
      return {
        labels: source.map((x) => x.label || x.name).filter(Boolean),
        data: source.map((x) => {
          const pct = x.audience_share_percentage ?? x.audienceSharePercentage;
          const rel = x.relative_relevance ?? x.relativeRelevance;
          return Math.round((pct != null ? pct : rel ?? 0) * 100);
        }),
      };
    }
  }
  if (typeof source === 'object' && source !== null && !Array.isArray(source)) {
    const values = Object.values(source);
    if (values.length > 0 && (values[0]?.label != null || typeof values[0] === 'object')) {
      return {
        labels: values.map((x) => (typeof x === 'object' ? x?.label : String(x))).filter(Boolean),
        data: values.map((x) => {
          if (typeof x !== 'object') return 0;
          const pct = x.audienceSharePercentage ?? x.audience_share_percentage;
          const rel = x.relativeRelevance ?? x.relative_relevance;
          return Math.round((pct != null ? pct : rel ?? 0) * 100);
        }),
      };
    }
  }
  return { labels: [], data: [] };
}

const barOptions = {
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    y: {
      grid: { color: 'rgba(16, 24, 40, 0.05)', drawBorder: false },
      ticks: { font: { size: 10 }, color: '#98A2B3' },
    },
    x: {
      grid: { display: false, drawBorder: false },
      ticks: { maxRotation: 45, minRotation: 45, font: { size: 10 }, color: '#667085' },
    },
  },
};

const barOptionsTruncated = {
  ...barOptions,
  scales: {
    ...barOptions.scales,
    x: {
      ...barOptions.scales.x,
      ticks: {
        ...barOptions.scales.x.ticks,
        callback: function (value) {
          const label = this.getLabelForValue(value);
          return typeof label === 'string' && label.length > 25 ? label.substring(0, 25) + '…' : label;
        },
      },
    },
  },
  plugins: {
    ...barOptions.plugins,
    tooltip: {
      callbacks: {
        title: (items) => items[0]?.label ?? '',
      },
    },
  },
};

/**
 * Marks a figure that came from the creator's own platform account rather
 * than a vendor's model. Every other number on this page is an estimate;
 * without saying which is which they look identical, and a brand has no way
 * to tell what it is deciding on.
 */
function MeasuredChip({ source }) {
  if (source !== 'first_party') return null;
  return (
    <Chip
      size="small"
      label="Verified"
      title="Measured from the creator's own account, not estimated"
      style={{
        height: 20,
        marginLeft: 8,
        fontSize: 11,
        fontWeight: 700,
        color: '#0A6B5C',
        backgroundColor: '#E7F6F0',
        border: '1px solid #BFE5D9',
      }}
    />
  );
}

function CopyButton({ onCopy }) {
  return (
    <IconButton onClick={onCopy} size="small">
      <ContentCopyIcon style={{ height: '0.6em', width: '0.6em', color: ui.inkFaint }} />
    </IconButton>
  );
}

function AudienceDetails({
  first_party_insight = {},
  audience_location_split = {},
  audience_interest = [],
  audience_languages = [],
  audience_age_split = [],
  audience_genders_per_age = [],
  audience_gender_split = [],
  audience_gender = {},
  insight_source = {},
  is_staff,
  list,
  brand_affinity = [],
  dump,
  bc_insight,
  bc_insight_60,
  bc_insight_90,
  bc_insight_lifetime,
  bc_public_data,
  period = '28',
  contentType = 'overall',
  is_brandconnect,
  platform,
}) {
  const [brandList, setBrandList] = useState([]);

  const dataSharingEnabled =
    String(bc_public_data?.dataSharingState || '').toUpperCase().includes('ENABLED') ||
    String(bc_public_data?.dataSharingState || '').toUpperCase().includes('GIVEN') ||
    String(dump?.data_sharing_state || '').toUpperCase().includes('ENABLED') ||
    String(dump?.data_sharing_state || '').toUpperCase().includes('GIVEN');

  const isBC = is_brandconnect === 'yes' && dataSharingEnabled;

  // First-party audience measured over the selected window. The collector
  // stores one entry per position of the period switch ('28'|'60'|'90'|
  // 'lifetime'), so changing the period changes these charts too instead
  // of silently showing 28-day figures under every filter.
  const fpAudience =
    (!isBC && first_party_insight?.audience?.[period]) || null;

  useEffect(() => {
    list(brandList);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandList]);

  const copyOnClick = (copyText) => navigator.clipboard.writeText(copyText);

  const appendToList = (item) => {
    if (!brandList.includes(item)) {
      setBrandList((prev) => [...prev, item]);
    }
  };

  const deleteItem = (item) => {
    setBrandList((prev) => prev.filter((i) => i !== item));
  };

  // ── BC source selection ──────────────────────────────────────────────────────
  const bcSource =
    period === '60' ? bc_insight_60
    : period === '90' ? bc_insight_90
    : period === 'lifetime' ? bc_insight_lifetime
    : bc_insight;

  const channelStatsKey =
    contentType === 'short' ? 'SHORTS'
    : contentType === 'video' ? 'VOD'
    : 'OVERALL';

  // ── Raw data selection ───────────────────────────────────────────────────────
  let genderSplit = isBC
    ? bcSource?.channelStats?.[channelStatsKey]?.audienceGenderDistribution
    : fpAudience?.genders || audience_gender_split;

  if (genderSplit && !Array.isArray(genderSplit) && typeof genderSplit === 'object') {
    genderSplit = Object.keys(genderSplit).length === 0 ? [] : Object.values(genderSplit);
  }

  const audienceAgeDistribution = isBC
    ? bcSource?.channelStats?.[channelStatsKey]?.audienceAgeDistribution
    : fpAudience?.ages || audience_age_split;

  const audienceCountryDistributions = isBC
    ? bcSource?.channelStats?.[channelStatsKey]?.audienceCountryDistributions
    : fpAudience?.location || audience_location_split;

  const watchInterestSource = isBC
    ? bcSource?.channelStats?.[channelStatsKey]?.audienceAffinityInterestDistribution
    : audience_interest;

  const shoppingInterestSource =
    bcSource?.channelStats?.[channelStatsKey]?.audienceInMarketInterestDistribution ||
    dump?.audience_shopping_interest_distribution;

  const affinityInterestSource =
    bcSource?.channelStats?.[channelStatsKey]?.audienceAffinityInterestDistribution;

  // ── Normalized data ──────────────────────────────────────────────────────────
  const normalizedGenderSplit = normalizeGenderSplit(genderSplit, bcSource, channelStatsKey);
  const normalizedAgeSplit = normalizeAgeSplit(audienceAgeDistribution);
  const normalizedLocation = normalizeLocationSplit(audienceCountryDistributions);
  const normalizedStateSplit = normalizeStateSplit(audienceCountryDistributions);
  const normalizedWatchInterest = normalizeInterestForBar(watchInterestSource);
  const normalizedShoppingInterest = normalizeInterestForBar(shoppingInterestSource);
  const normalizedAffinityInterest = normalizeInterestForBar(affinityInterestSource);

  // ── Primary gender label ─────────────────────────────────────────────────────
  const primaryGenderEntry =
    normalizedGenderSplit.length > 0
      ? [...normalizedGenderSplit].sort((a, b) => (b?.weight ?? 0) - (a?.weight ?? 0))[0]
      : null;

  const primaryGenderLabel =
    primaryGenderEntry?.code === 'MALE' ? 'Male'
    : primaryGenderEntry?.code === 'FEMALE' ? 'Female'
    : primaryGenderEntry?.code === 'USER_SPECIFIED' ? 'User specified'
    : primaryGenderEntry?.code ?? null;

  const primaryGenderPct =
    primaryGenderEntry?.weight != null && !isNaN(Number(primaryGenderEntry.weight))
      ? `${Math.round(Number(primaryGenderEntry.weight) * 100)}%`
      : null;

  const NoDataPeriod = () => (
    <EmptyState>
      No Data Available in this {period === 'lifetime' ? 'lifetime' : `${period} days`} duration
    </EmptyState>
  );

  const BarChart = ({ labels, data, label, options = barOptions }) => (
    <Bar
      data={{
        labels,
        datasets: [
          {
            label,
            data,
            backgroundColor: 'rgba(79, 70, 229, 0.65)',
            hoverBackgroundColor: 'rgba(79, 70, 229, 0.9)',
            borderRadius: 5,
            maxBarThickness: 34,
          },
        ],
      }}
      options={options}
    />
  );

  const columnStyle = { display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 };

  return (
    <SectionCard
      icon={<GroupsRoundedIcon fontSize="small" />}
      title="Audience details"
      subtitle="Who follows and watches this creator"
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, alignItems: 'start' }}>
        {/* ════ LEFT COLUMN ════ */}
        <div style={columnStyle}>
          {/* Gender Split */}
          {normalizedGenderSplit.length > 0 && (
            <ChartTile
              icon={<WcRoundedIcon style={{ fontSize: 17 }} />}
              title={
                <>
                  Audience gender split
                  <MeasuredChip source={insight_source?.audience_gender_split} />
                </>
              }
              right={
                is_staff ? (
                  <CopyButton onCopy={() => copyOnClick(normalizedGenderSplit.map((e) => `${e.code} (${Math.round(e.weight * 100)}%)`))} />
                ) : null
              }
              height={330}
            >
              <div style={{ position: 'relative', height: '100%' }}>
                <Doughnut
                  data={{
                    labels: normalizedGenderSplit.map((entry) => {
                      const label =
                        entry.code === 'MALE' ? 'Male'
                        : entry.code === 'FEMALE' ? 'Female'
                        : entry.code === 'USER_SPECIFIED' ? 'User specified'
                        : entry.code;
                      return `${label} (${Math.round(entry.weight * 100)}%)`;
                    }),
                    datasets: [
                      {
                        label: 'Gender',
                        data: normalizedGenderSplit.map((e) => Math.round(e.weight * 100)),
                        backgroundColor: ['#4F46E5', '#EC4899', '#F59E0B'],
                        hoverBackgroundColor: ['#4338CA', '#DB2777', '#D97706'],
                        borderWidth: 3,
                        borderColor: '#FFFFFF',
                      },
                    ],
                  }}
                  options={{
                    maintainAspectRatio: false,
                    cutout: '70%',
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, font: { size: 11 }, color: '#475467' },
                      },
                    },
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '40%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <div style={{ fontSize: 20, fontWeight: 800, color: ui.ink, fontFamily: ui.font }}>
                    {primaryGenderPct || '–'}
                  </div>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: ui.inkFaint, fontFamily: ui.font }}>
                    {primaryGenderLabel || '–'}
                  </div>
                </div>
              </div>
            </ChartTile>
          )}

          {/* Location Split */}
          <ChartTile
            icon={<PublicRoundedIcon style={{ fontSize: 16 }} />}
            title={
              <>
                Audience location split
                <MeasuredChip source={insight_source?.audience_location_split} />
              </>
            }
            right={
              is_staff && normalizedLocation.labels.length > 0 ? (
                <CopyButton onCopy={() => copyOnClick(normalizedLocation.labels.map((l, i) => `${l} (${normalizedLocation.data[i] || 0}%)`))} />
              ) : null
            }
            height={250}
          >
            {normalizedLocation.labels.length > 0 ? (
              <BarChart labels={normalizedLocation.labels} data={normalizedLocation.data} label="Location Split" />
            ) : (
              <EmptyState>No Data Available</EmptyState>
            )}
          </ChartTile>

          {/* Watch Interest — youtube + isBC only */}
          {platform === 'youtube' && isBC && (
            <ChartTile
              icon={<InterestsRoundedIcon style={{ fontSize: 16 }} />}
              title="Affinity interest"
              height={330}
            >
              {normalizedWatchInterest.labels.length > 0 ? (
                <BarChart labels={normalizedWatchInterest.labels} data={normalizedWatchInterest.data} label="Watch Interest" options={barOptionsTruncated} />
              ) : (
                <NoDataPeriod />
              )}
            </ChartTile>
          )}
        </div>

        {/* ════ RIGHT COLUMN ════ */}
        <div style={columnStyle}>
          {/* Language */}
          {audience_languages && Array.isArray(audience_languages) && audience_languages.length > 0 && (
            <ChartTile
              icon={<TranslateRoundedIcon style={{ fontSize: 15 }} />}
              title="Language"
              right={
                is_staff ? (
                  <CopyButton onCopy={() => copyOnClick(audience_languages.slice(0, 3).map((l) => `${l.name} (${l.percentage})`))} />
                ) : null
              }
            >
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {audience_languages.slice(0, 3).map((language, i) => (
                  <div
                    key={i}
                    style={{
                      flex: '1 1 90px',
                      background: ui.tileBg,
                      border: `1px solid ${ui.borderSoft}`,
                      borderRadius: 10,
                      padding: '10px 14px',
                      textAlign: 'center',
                      fontFamily: ui.font,
                    }}
                  >
                    <div style={{ fontSize: 16, fontWeight: 800, color: ui.primary }}>{language.percentage}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: ui.inkSoft, marginTop: 2 }}>{language.name}</div>
                  </div>
                ))}
              </div>
            </ChartTile>
          )}

          {/* Age Split */}
          <ChartTile
            icon={<CakeRoundedIcon style={{ fontSize: 15 }} />}
            title={
              <>
                Audience age split
                <MeasuredChip source={insight_source?.audience_age_split} />
              </>
            }
            right={
              is_staff && normalizedAgeSplit.length > 0 ? (
                <CopyButton onCopy={() => copyOnClick(normalizedAgeSplit.map((a) => `${a.code} (${Math.round(a.weight * 100)}%)`))} />
              ) : null
            }
            height={250}
          >
            {normalizedAgeSplit.length > 0 ? (
              <BarChart
                labels={normalizedAgeSplit.map((a) => a.code)}
                data={normalizedAgeSplit.map((a) => Math.round(a.weight * 100))}
                label="Age Split"
              />
            ) : (
              <EmptyState>No Data Available</EmptyState>
            )}
          </ChartTile>

          {/* State Split */}
          {normalizedStateSplit.labels.length > 0 && (
            <ChartTile
              icon={<MapRoundedIcon style={{ fontSize: 16 }} />}
              title="Audience state split (India)"
              right={
                is_staff ? (
                  <CopyButton onCopy={() => copyOnClick(normalizedStateSplit.labels.map((l, i) => `${l} (${normalizedStateSplit.data[i] || 0}%)`))} />
                ) : null
              }
              height={230}
            >
              <BarChart labels={normalizedStateSplit.labels} data={normalizedStateSplit.data} label="State Split" />
            </ChartTile>
          )}

          {/* Shopping Interest — youtube + isBC only, right column */}
          {platform === 'youtube' && isBC && (
            <ChartTile
              icon={<StorefrontRoundedIcon style={{ fontSize: 16 }} />}
              title="Market interest"
              height={330}
            >
              {normalizedShoppingInterest.labels.length > 0 ? (
                <BarChart labels={normalizedShoppingInterest.labels} data={normalizedShoppingInterest.data} label="Shopping Interest" options={barOptionsTruncated} />
              ) : (
                <NoDataPeriod />
              )}
            </ChartTile>
          )}

          {/* Audience Interest — hidden for youtube brandconnect, shown for all non-youtube */}
          {platform !== 'youtube' && (
            <ChartTile
              icon={<InterestsRoundedIcon style={{ fontSize: 16 }} />}
              title="Audience interest"
              right={
                is_staff && (!isBC
                  ? Array.isArray(audience_interest) && audience_interest.length > 0
                  : normalizedAffinityInterest.labels.length > 0) ? (
                  <CopyButton
                    onCopy={() =>
                      copyOnClick(
                        !isBC
                          ? audience_interest.map((item) => item.name)
                          : normalizedAffinityInterest.labels.map((l, i) => `${l} (${normalizedAffinityInterest.data[i] || 0}%)`)
                      )
                    }
                  />
                ) : null
              }
              height={250}
            >
              {!isBC ? (
                Array.isArray(audience_interest) && audience_interest.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', height: '100%' }}>
                    {audience_interest.map((item, idx) => {
                      const IconComponent = audienceIcons[item.name];
                      return (
                        <div
                          key={item.id ?? idx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            background: ui.tileBg,
                            border: `1px solid ${ui.borderSoft}`,
                            borderRadius: 10,
                            padding: '8px 12px',
                            flex: '0 0 auto',
                          }}
                        >
                          {IconComponent && <img src={IconComponent} alt={item.name} style={{ width: 18, height: 18 }} />}
                          <span style={{ fontSize: 13, fontWeight: 700, color: ui.inkSoft, fontFamily: ui.font }}>
                            {item.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState>No Data Available</EmptyState>
                )
              ) : normalizedAffinityInterest.labels.length > 0 ? (
                <BarChart labels={normalizedAffinityInterest.labels} data={normalizedAffinityInterest.data} label="Audience Interest" />
              ) : (
                <NoDataPeriod />
              )}
            </ChartTile>
          )}
        </div>
      </div>

      {/* ── Brand Affinity ── */}
      {Array.isArray(brand_affinity) && brand_affinity.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <ChartTile
            icon={<LoyaltyRoundedIcon style={{ fontSize: 16 }} />}
            title="Influencer brand affinity"
            right={
              is_staff ? (
                <CopyButton onCopy={() => copyOnClick(brand_affinity.map((b) => b.name))} />
              ) : null
            }
          >
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, scrollbarWidth: 'thin' }}>
              {brand_affinity.map((brand, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => appendToList(brand.name)}
                  style={{
                    flex: '0 0 auto',
                    cursor: 'pointer',
                    borderRadius: 999,
                    border: `1px solid ${ui.border}`,
                    background: '#FFFFFF',
                    color: ui.inkSoft,
                    padding: '6px 14px',
                    fontSize: 12.5,
                    fontWeight: 700,
                    fontFamily: ui.font,
                    whiteSpace: 'nowrap',
                    transition: 'border-color 140ms ease, color 140ms ease',
                  }}
                >
                  {brand.name}
                </button>
              ))}
            </div>
            {brandList.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                {brandList.map((val) => (
                  <Chip
                    key={val}
                    variant="outlined"
                    chipcolor="secondary"
                    label={val}
                    sx={{
                      '& .MuiChip-label': { fontSize: '0.7rem', fontWeight: 700 },
                      '& .MuiChip-deleteIcon': { height: '15px', width: '15px' },
                      height: '25px',
                    }}
                    onDelete={() => deleteItem(val)}
                  />
                ))}
              </div>
            )}
          </ChartTile>
        </div>
      )}
    </SectionCard>
  );
}

export default AudienceDetails;
